import { Link } from 'react-router-dom';

/**
 * Polymorphic action element.
 *
 * Renders an <a> for internal routes and mail/telephone links, and a real
 * <button> for in-page actions. This matters for accessibility: wrapping a
 * <button> in an <a> (or the reverse) produces invalid markup and confusing
 * announcements for screen-reader and keyboard users.
 *
 * Variants: primary | secondary | ghost | inverse
 * Sizes:    sm | md | lg
 */
const Button = ({
  children,
  to,
  href,
  onClick,
  type = 'button',
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  disabled = false,
  ariaLabel,
  className = '',
  external = false,
}) => {
  const classes = [
    'btn',
    `btn--${variant}`,
    `btn--${size}`,
    fullWidth ? 'btn--block' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const commonProps = {
    className: classes,
    'aria-label': ariaLabel || undefined,
  };

  // Internal client-side route
  if (to && !disabled) {
    return (
      <Link to={to} {...commonProps}>
        {children}
      </Link>
    );
  }

  // External or protocol link (mailto:, tel:, https:)
  if (href && !disabled) {
    const externalProps = external
      ? { target: '_blank', rel: 'noopener noreferrer' }
      : {};
    return (
      <a href={href} {...externalProps} {...commonProps}>
        {children}
      </a>
    );
  }

  return (
    <button type={type} onClick={onClick} disabled={disabled} {...commonProps}>
      {children}
    </button>
  );
};

export default Button;
